import React, { useState, useEffect, useContext } from 'react';
import api from '../../services/api';
import { AuthContext } from '../../context/AuthContext';

const Mesas = () => {
    const { user } = useContext(AuthContext);

    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sectionFilter, setSectionFilter] = useState('all');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'

    const [showModal, setShowModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    const initialFormData = {
        id: null,
        number: '',
        name: '',
        capacity: 4,
        status: 'available',
        section: 'Interior',
        floor: 1,
        is_vip: false,
        is_active: true,
        notes: ''
    };

    const [formData, setFormData] = useState(initialFormData);

    useEffect(() => {
        fetchTables();
    }, []);

    const fetchTables = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/pos/tables/');
            const data = response.data.results || response.data || [];
            // Sort tables naturally by number/name
            const sorted = [...data].sort((a, b) => {
                const numA = parseInt(a.number, 10);
                const numB = parseInt(b.number, 10);
                if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                return String(a.number).localeCompare(String(b.number));
            });
            setTables(sorted);
            setError(null);
        } catch (err) {
            console.error('Error fetching tables:', err);
            setError('Error al cargar la lista de mesas');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : (type === 'number' ? (parseInt(value, 10) || 1) : value)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.number.trim()) {
            alert('Por favor ingrese el número o identificador de la mesa');
            return;
        }

        try {
            setIsSaving(true);
            const payload = {
                number: formData.number.trim(),
                name: formData.name.trim() || `Mesa ${formData.number.trim()}`,
                capacity: parseInt(formData.capacity, 10) || 4,
                status: formData.status,
                section: formData.section.trim() || 'Interior',
                floor: parseInt(formData.floor, 10) || 1,
                is_vip: !!formData.is_vip,
                is_active: !!formData.is_active,
                notes: formData.notes.trim()
            };

            if (formData.id) {
                await api.put(`/api/pos/tables/${formData.id}/`, payload);
            } else {
                await api.post('/api/pos/tables/', payload);
            }

            setShowModal(false);
            setFormData(initialFormData);
            await fetchTables();
        } catch (err) {
            console.error('Error saving table:', err);
            const errData = err.response?.data;
            let errMsg = 'Error al guardar la mesa';
            if (errData?.number) {
                errMsg = Array.isArray(errData.number) ? errData.number.join(', ') : errData.number;
            } else if (errData?.error) {
                errMsg = errData.error;
            }
            alert(errMsg);
        } finally {
            setIsSaving(false);
        }
    };

    const openEditModal = (table) => {
        setFormData({
            id: table.id,
            number: table.number || '',
            name: table.name || '',
            capacity: table.capacity || 4,
            status: table.status || 'available',
            section: table.section || 'Interior',
            floor: table.floor || 1,
            is_vip: !!table.is_vip,
            is_active: table.is_active !== undefined ? table.is_active : true,
            notes: table.notes || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (table) => {
        if (!window.confirm(`¿Está seguro de eliminar la Mesa ${table.number}?`)) {
            return;
        }

        try {
            await api.delete(`/api/pos/tables/${table.id}/`);
            await fetchTables();
        } catch (err) {
            console.error('Error deleting table:', err);
            alert('Error al eliminar la mesa. Asegúrese de que no tenga órdenes activas asociadas.');
        }
    };

    const handleFreeTable = async (table) => {
        if (!window.confirm(`¿Desea liberar la Mesa ${table.number} y marcarla como disponible?`)) {
            return;
        }

        try {
            await api.post(`/api/pos/tables/${table.id}/free/`);
            await fetchTables();
        } catch (err) {
            console.error('Error freeing table:', err);
            // Fallback: update status to available directly
            try {
                await api.patch(`/api/pos/tables/${table.id}/`, { status: 'available' });
                await fetchTables();
            } catch (err2) {
                alert('No se pudo liberar la mesa');
            }
        }
    };

    const handleQuickStatusChange = async (table, newStatus) => {
        try {
            await api.patch(`/api/pos/tables/${table.id}/`, { status: newStatus });
            await fetchTables();
        } catch (err) {
            console.error('Error updating table status:', err);
            alert('Error al actualizar el estado de la mesa');
        }
    };

    const handleGenerateInitialTables = async () => {
        if (!window.confirm('¿Desea crear automáticamente 10 mesas iniciales numeradas del 1 al 10?')) {
            return;
        }

        try {
            setIsGenerating(true);
            const promises = [];
            for (let i = 1; i <= 10; i++) {
                const numStr = String(i);
                // Check if already exists
                const exists = tables.some(t => String(t.number) === numStr);
                if (!exists) {
                    promises.push(
                        api.post('/api/pos/tables/', {
                            number: numStr,
                            name: `Mesa ${numStr}`,
                            capacity: i <= 4 ? 4 : (i <= 8 ? 6 : 8),
                            status: 'available',
                            section: i <= 6 ? 'Interior' : 'Terraza',
                            floor: 1,
                            is_active: true,
                            is_vip: false,
                            notes: ''
                        })
                    );
                }
            }
            await Promise.all(promises);
            await fetchTables();
        } catch (err) {
            console.error('Error generating initial tables:', err);
            alert('Error al generar las mesas iniciales');
        } finally {
            setIsGenerating(false);
        }
    };

    // Filter Logic
    const availableSections = Array.from(new Set(tables.map(t => t.section).filter(Boolean)));

    const filteredTables = tables.filter(table => {
        const matchesSearch = 
            String(table.number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(table.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(table.section || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(table.waiter_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(table.current_order_number || '').toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'all' || table.status === statusFilter;
        const matchesSection = sectionFilter === 'all' || table.section === sectionFilter;

        return matchesSearch && matchesStatus && matchesSection;
    });

    // Counts for stats
    const totalCount = tables.length;
    const availableCount = tables.filter(t => t.status === 'available' && t.is_active).length;
    const occupiedCount = tables.filter(t => t.status === 'occupied').length;
    const maintenanceCount = tables.filter(t => ['cleaning', 'maintenance'].includes(t.status)).length;

    const getStatusInfo = (status) => {
        switch (status) {
            case 'available':
                return { label: 'Disponible', bg: '#dcfce7', text: '#15803d', border: '#86efac', icon: 'bi-check-circle-fill' };
            case 'occupied':
                return { label: 'Ocupada', bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5', icon: 'bi-people-fill' };
            case 'reserved':
                return { label: 'Reservada', bg: '#f3e8ff', text: '#7e22ce', border: '#d8b4fe', icon: 'bi-bookmark-star-fill' };
            case 'cleaning':
                return { label: 'Limpiando', bg: '#e0f2fe', text: '#0369a1', border: '#7dd3fc', icon: 'bi-stars' };
            case 'maintenance':
                return { label: 'Mantenimiento', bg: '#f3f4f6', text: '#4b5563', border: '#d1d5db', icon: 'bi-tools' };
            default:
                return { label: status || 'Desconocido', bg: '#f3f4f6', text: '#4b5563', border: '#e5e7eb', icon: 'bi-circle' };
        }
    };

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#FAF4EB', padding: '24px' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>

                {/* Header Title & Actions */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px',
                    marginBottom: '24px'
                }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: '46px',
                                height: '46px',
                                borderRadius: '12px',
                                backgroundColor: '#603833',
                                color: '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '22px',
                                boxShadow: '0 4px 10px rgba(96, 56, 51, 0.2)'
                            }}>
                                <i className="bi bi-grid-3x3-gap-fill"></i>
                            </div>
                            <div>
                                <h1 style={{
                                    fontSize: '24px',
                                    fontWeight: '800',
                                    color: '#603833',
                                    margin: 0,
                                    letterSpacing: '-0.02em'
                                }}>
                                    Gestión de Mesas
                                </h1>
                                <p style={{ margin: 0, fontSize: '13px', color: '#78605c' }}>
                                    Control de mesas, capacidades, ubicación y disponibilidad en tiempo real
                                </p>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {tables.length === 0 && (
                            <button
                                onClick={handleGenerateInitialTables}
                                disabled={isGenerating}
                                style={{
                                    padding: '10px 18px',
                                    backgroundColor: '#FAF4EB',
                                    color: '#603833',
                                    border: '1px solid #cda367',
                                    borderRadius: '10px',
                                    fontWeight: '700',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'all 0.2s',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
                                }}
                            >
                                <i className="bi bi-magic"></i>
                                {isGenerating ? 'Generando...' : 'Crear 10 Mesas Rápidas'}
                            </button>
                        )}

                        <button
                            onClick={() => {
                                setFormData(initialFormData);
                                setShowModal(true);
                            }}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#603833',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '10px',
                                fontWeight: '700',
                                fontSize: '14px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s',
                                boxShadow: '0 4px 12px rgba(96, 56, 51, 0.25)'
                            }}
                        >
                            <i className="bi bi-plus-circle-fill"></i>
                            Nueva Mesa
                        </button>
                    </div>
                </div>

                {/* Metric Summary Cards */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                    marginBottom: '24px'
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        padding: '18px 20px',
                        borderRadius: '14px',
                        border: '1px solid #e8ded1',
                        boxShadow: '0 2px 6px rgba(96, 56, 51, 0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#78605c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Mesas</div>
                            <div style={{ fontSize: '28px', fontWeight: '800', color: '#603833', marginTop: '4px' }}>{totalCount}</div>
                        </div>
                        <div style={{ width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#FAF4EB', color: '#603833', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                            <i className="bi bi-grid-fill"></i>
                        </div>
                    </div>

                    <div style={{
                        backgroundColor: '#ffffff',
                        padding: '18px 20px',
                        borderRadius: '14px',
                        border: '1px solid #bbf7d0',
                        boxShadow: '0 2px 6px rgba(21, 128, 61, 0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Disponibles</div>
                            <div style={{ fontSize: '28px', fontWeight: '800', color: '#15803d', marginTop: '4px' }}>{availableCount}</div>
                        </div>
                        <div style={{ width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#dcfce7', color: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                            <i className="bi bi-check-circle-fill"></i>
                        </div>
                    </div>

                    <div style={{
                        backgroundColor: '#ffffff',
                        padding: '18px 20px',
                        borderRadius: '14px',
                        border: '1px solid #fecaca',
                        boxShadow: '0 2px 6px rgba(185, 28, 28, 0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ocupadas</div>
                            <div style={{ fontSize: '28px', fontWeight: '800', color: '#b91c1c', marginTop: '4px' }}>{occupiedCount}</div>
                        </div>
                        <div style={{ width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#fee2e2', color: '#b91c1c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                            <i className="bi bi-people-fill"></i>
                        </div>
                    </div>

                    <div style={{
                        backgroundColor: '#ffffff',
                        padding: '18px 20px',
                        borderRadius: '14px',
                        border: '1px solid #bae6fd',
                        boxShadow: '0 2px 6px rgba(3, 105, 161, 0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Limpieza / Mant.</div>
                            <div style={{ fontSize: '28px', fontWeight: '800', color: '#0369a1', marginTop: '4px' }}>{maintenanceCount}</div>
                        </div>
                        <div style={{ width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#e0f2fe', color: '#0369a1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                            <i className="bi bi-stars"></i>
                        </div>
                    </div>
                </div>

                {/* Filters and View Switcher */}
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '14px',
                    padding: '16px 20px',
                    marginBottom: '20px',
                    border: '1px solid #e8ded1',
                    boxShadow: '0 2px 6px rgba(96, 56, 51, 0.03)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '14px',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', flex: 1, minWidth: '300px' }}>
                        {/* Search Input */}
                        <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
                            <i className="bi bi-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}></i>
                            <input
                                type="text"
                                placeholder="Buscar por número, nombre o sección..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '9px 12px 9px 36px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    outline: 'none',
                                    transition: 'all 0.2s'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#603833';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(96, 56, 51, 0.12)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#d1d5db';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>

                        {/* Status Filter */}
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{
                                padding: '9px 14px',
                                borderRadius: '8px',
                                border: '1px solid #d1d5db',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: '#374151',
                                backgroundColor: '#ffffff',
                                cursor: 'pointer',
                                outline: 'none'
                            }}
                        >
                            <option value="all">Todos los estados</option>
                            <option value="available">Disponibles</option>
                            <option value="occupied">Ocupadas</option>
                            <option value="reserved">Reservadas</option>
                            <option value="cleaning">Limpiando</option>
                            <option value="maintenance">Mantenimiento</option>
                        </select>

                        {/* Section Filter */}
                        {availableSections.length > 0 && (
                            <select
                                value={sectionFilter}
                                onChange={(e) => setSectionFilter(e.target.value)}
                                style={{
                                    padding: '9px 14px',
                                    borderRadius: '8px',
                                    border: '1px solid #d1d5db',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    color: '#374151',
                                    backgroundColor: '#ffffff',
                                    cursor: 'pointer',
                                    outline: 'none'
                                }}
                            >
                                <option value="all">Todas las secciones</option>
                                {availableSections.map(sec => (
                                    <option key={sec} value={sec}>{sec}</option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* View Switcher buttons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: '#FAF4EB', padding: '4px', borderRadius: '8px', border: '1px solid #e8ded1' }}>
                        <button
                            onClick={() => setViewMode('grid')}
                            style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: 'none',
                                backgroundColor: viewMode === 'grid' ? '#603833' : 'transparent',
                                color: viewMode === 'grid' ? '#ffffff' : '#603833',
                                fontWeight: '700',
                                fontSize: '12px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.2s'
                            }}
                        >
                            <i className="bi bi-grid-fill"></i> Cuadrícula
                        </button>
                        <button
                            onClick={() => setViewMode('table')}
                            style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: 'none',
                                backgroundColor: viewMode === 'table' ? '#603833' : 'transparent',
                                color: viewMode === 'table' ? '#ffffff' : '#603833',
                                fontWeight: '700',
                                fontSize: '12px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.2s'
                            }}
                        >
                            <i className="bi bi-list-ul"></i> Tabla
                        </button>
                    </div>
                </div>

                {/* Main Content Area */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', backgroundColor: '#ffffff', borderRadius: '14px', border: '1px solid #e8ded1' }}>
                        <div className="spinner-border" style={{ color: '#603833', width: '3rem', height: '3rem' }} role="status"></div>
                        <p style={{ marginTop: '16px', color: '#78605c', fontWeight: '600' }}>Cargando mesas...</p>
                    </div>
                ) : error ? (
                    <div style={{ padding: '24px', backgroundColor: '#fee2e2', color: '#b91c1c', borderRadius: '12px', border: '1px solid #fca5a5', textAlign: 'center' }}>
                        <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '24px', marginRight: '8px' }}></i>
                        {error}
                    </div>
                ) : filteredTables.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        backgroundColor: '#ffffff',
                        borderRadius: '14px',
                        border: '1px solid #e8ded1'
                    }}>
                        <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#FAF4EB', color: '#603833', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', margin: '0 auto 16px auto' }}>
                            <i className="bi bi-grid"></i>
                        </div>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#603833', marginBottom: '8px' }}>
                            {searchTerm || statusFilter !== 'all' ? 'No se encontraron mesas con esos filtros' : 'No hay mesas registradas'}
                        </h3>
                        <p style={{ color: '#78605c', fontSize: '14px', maxWidth: '400px', margin: '0 auto 20px auto' }}>
                            {searchTerm || statusFilter !== 'all' ? 'Intente ajustar su búsqueda o filtros' : 'Cree su primera mesa o genere el lote inicial para comenzar.'}
                        </p>
                        {tables.length === 0 && (
                            <button
                                onClick={handleGenerateInitialTables}
                                disabled={isGenerating}
                                style={{
                                    padding: '10px 22px',
                                    backgroundColor: '#603833',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '10px',
                                    fontWeight: '700',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    boxShadow: '0 4px 10px rgba(96, 56, 51, 0.2)'
                                }}
                            >
                                <i className="bi bi-magic"></i>
                                Crear 10 Mesas Iniciales
                            </button>
                        )}
                    </div>
                ) : viewMode === 'grid' ? (
                    /* GRID VIEW */
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: '20px'
                    }}>
                        {filteredTables.map(table => {
                            const statusInfo = getStatusInfo(table.status);
                            const isOccupied = table.status === 'occupied';

                            return (
                                <div
                                    key={table.id}
                                    style={{
                                        backgroundColor: '#ffffff',
                                        borderRadius: '14px',
                                        border: `1px solid ${isOccupied ? '#fca5a5' : '#e8ded1'}`,
                                        boxShadow: isOccupied ? '0 4px 14px rgba(220, 38, 38, 0.08)' : '0 2px 6px rgba(96, 56, 51, 0.04)',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'all 0.2s ease',
                                        position: 'relative'
                                    }}
                                >
                                    {/* Top Card Header */}
                                    <div style={{
                                        padding: '16px',
                                        borderBottom: '1px solid #f1f5f9',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        backgroundColor: isOccupied ? '#fef2f2' : '#ffffff'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <div style={{
                                                width: '42px',
                                                height: '42px',
                                                borderRadius: '10px',
                                                backgroundColor: isOccupied ? '#b91c1c' : '#603833',
                                                color: '#ffffff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: '800',
                                                fontSize: '18px',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                                            }}>
                                                #{table.number}
                                            </div>
                                            <div>
                                                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#1e293b' }}>
                                                    {table.name || `Mesa ${table.number}`}
                                                </h4>
                                                <span style={{ fontSize: '12px', color: '#64748b' }}>
                                                    {table.section || 'Interior'} • Piso {table.floor || 1}
                                                </span>
                                            </div>
                                        </div>

                                        <span style={{
                                            padding: '4px 10px',
                                            borderRadius: '20px',
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            backgroundColor: statusInfo.bg,
                                            color: statusInfo.text,
                                            border: `1px solid ${statusInfo.border}`,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            <i className={`bi ${statusInfo.icon}`}></i>
                                            {statusInfo.label}
                                        </span>
                                    </div>

                                    {/* Card Details Body */}
                                    <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#475569' }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                <i className="bi bi-people" style={{ color: '#603833' }}></i> Capacidad:
                                            </span>
                                            <span style={{ fontWeight: '700', color: '#1e293b' }}>{table.capacity} personas</span>
                                        </div>

                                        {table.is_vip && (
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#b45309', backgroundColor: '#fef3c7', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', width: 'fit-content' }}>
                                                <i className="bi bi-star-fill"></i> Mesa VIP
                                            </div>
                                        )}

                                        {isOccupied && table.current_order_number && (
                                            <div style={{ backgroundColor: '#fee2e2', padding: '8px 12px', borderRadius: '8px', border: '1px solid #fca5a5' }}>
                                                <div style={{ fontSize: '11px', fontWeight: '700', color: '#991b1b', textTransform: 'uppercase' }}>Orden en curso</div>
                                                <div style={{ fontSize: '13px', fontWeight: '800', color: '#b91c1c' }}>{table.current_order_number}</div>
                                                {table.waiter_name && (
                                                    <div style={{ fontSize: '11px', color: '#7f1d1d' }}>Atendido por: {table.waiter_name}</div>
                                                )}
                                            </div>
                                        )}

                                        {table.notes && (
                                            <div style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic', backgroundColor: '#f8fafc', padding: '6px 10px', borderRadius: '6px' }}>
                                                "{table.notes}"
                                            </div>
                                        )}
                                    </div>

                                    {/* Card Actions Footer */}
                                    <div style={{
                                        padding: '12px 16px',
                                        backgroundColor: '#fafaf9',
                                        borderTop: '1px solid #f1f5f9',
                                        display: 'flex',
                                        gap: '8px',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        {isOccupied ? (
                                            <button
                                                onClick={() => handleFreeTable(table)}
                                                style={{
                                                    flex: 1,
                                                    padding: '7px 12px',
                                                    backgroundColor: '#15803d',
                                                    color: '#ffffff',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    fontSize: '12px',
                                                    fontWeight: '700',
                                                    cursor: 'pointer',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '5px'
                                                }}
                                            >
                                                <i className="bi bi-door-open-fill"></i> Liberar Mesa
                                            </button>
                                        ) : (
                                            <div style={{ display: 'flex', gap: '4px', flex: 1 }}>
                                                {table.status !== 'available' && (
                                                    <button
                                                        onClick={() => handleQuickStatusChange(table, 'available')}
                                                        title="Marcar como disponible"
                                                        style={{
                                                            padding: '6px 10px',
                                                            backgroundColor: '#dcfce7',
                                                            color: '#15803d',
                                                            border: '1px solid #86efac',
                                                            borderRadius: '6px',
                                                            fontSize: '11px',
                                                            fontWeight: '700',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Disponible
                                                    </button>
                                                )}
                                                {table.status !== 'cleaning' && (
                                                    <button
                                                        onClick={() => handleQuickStatusChange(table, 'cleaning')}
                                                        title="Marcar en limpieza"
                                                        style={{
                                                            padding: '6px 10px',
                                                            backgroundColor: '#e0f2fe',
                                                            color: '#0369a1',
                                                            border: '1px solid #7dd3fc',
                                                            borderRadius: '6px',
                                                            fontSize: '11px',
                                                            fontWeight: '700',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Limpiar
                                                    </button>
                                                )}
                                                {table.status !== 'maintenance' && (
                                                    <button
                                                        onClick={() => handleQuickStatusChange(table, 'maintenance')}
                                                        title="Marcar en mantenimiento"
                                                        style={{
                                                            padding: '6px 10px',
                                                            backgroundColor: '#f3f4f6',
                                                            color: '#4b5563',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '6px',
                                                            fontSize: '11px',
                                                            fontWeight: '700',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Mant.
                                                    </button>
                                                )}
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <button
                                                onClick={() => openEditModal(table)}
                                                title="Editar mesa"
                                                style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #d1d5db',
                                                    backgroundColor: '#ffffff',
                                                    color: '#374151',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '13px'
                                                }}
                                            >
                                                <i className="bi bi-pencil-fill"></i>
                                            </button>

                                            <button
                                                onClick={() => handleDelete(table)}
                                                title="Eliminar mesa"
                                                style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #fecaca',
                                                    backgroundColor: '#fef2f2',
                                                    color: '#ef4444',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '13px'
                                                }}
                                            >
                                                <i className="bi bi-trash-fill"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* TABLE VIEW */
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '14px',
                        border: '1px solid #e8ded1',
                        overflow: 'hidden',
                        boxShadow: '0 2px 6px rgba(96, 56, 51, 0.04)'
                    }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{ backgroundColor: '#603833', color: '#ffffff' }}>
                                    <tr>
                                        <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>N° Mesa</th>
                                        <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>Nombre Descriptivo</th>
                                        <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>Sección / Piso</th>
                                        <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>Capacidad</th>
                                        <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>Estado</th>
                                        <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>Orden en Curso</th>
                                        <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTables.map(table => {
                                        const statusInfo = getStatusInfo(table.status);
                                        return (
                                            <tr key={table.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                                                    <span style={{
                                                        padding: '4px 10px',
                                                        borderRadius: '6px',
                                                        backgroundColor: '#603833',
                                                        color: '#ffffff',
                                                        fontWeight: '800',
                                                        fontSize: '13px'
                                                    }}>
                                                        #{table.number}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '14px 16px', fontWeight: '700', color: '#1e293b' }}>
                                                    {table.name || `Mesa ${table.number}`}
                                                    {table.is_vip && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#b45309', backgroundColor: '#fef3c7', padding: '2px 6px', borderRadius: '4px' }}>VIP</span>}
                                                </td>
                                                <td style={{ padding: '14px 16px', color: '#64748b', fontSize: '13px' }}>
                                                    {table.section || 'Interior'} (Piso {table.floor || 1})
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#334155' }}>
                                                    {table.capacity} personas
                                                </td>
                                                <td style={{ padding: '14px 16px' }}>
                                                    <span style={{
                                                        padding: '4px 10px',
                                                        borderRadius: '12px',
                                                        fontSize: '11px',
                                                        fontWeight: '700',
                                                        backgroundColor: statusInfo.bg,
                                                        color: statusInfo.text,
                                                        border: `1px solid ${statusInfo.border}`,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}>
                                                        <i className={`bi ${statusInfo.icon}`}></i>
                                                        {statusInfo.label}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '14px 16px', fontSize: '13px' }}>
                                                    {table.current_order_number ? (
                                                        <span style={{ fontWeight: '700', color: '#dc2626' }}>{table.current_order_number}</span>
                                                    ) : (
                                                        <span style={{ color: '#94a3b8' }}>-</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                    <div style={{ display: 'inline-flex', gap: '6px' }}>
                                                        {table.status === 'occupied' && (
                                                            <button
                                                                onClick={() => handleFreeTable(table)}
                                                                title="Liberar mesa"
                                                                style={{ padding: '5px 10px', backgroundColor: '#15803d', color: '#ffffff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
                                                            >
                                                                Liberar
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => openEditModal(table)}
                                                            title="Editar mesa"
                                                            style={{ padding: '5px 8px', backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', color: '#334155' }}
                                                        >
                                                            <i className="bi bi-pencil-fill"></i>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(table)}
                                                            title="Eliminar mesa"
                                                            style={{ padding: '5px 8px', backgroundColor: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', cursor: 'pointer', color: '#ef4444' }}
                                                        >
                                                            <i className="bi bi-trash-fill"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* MODAL CREAR / EDITAR MESA */}
                {showModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        padding: '16px'
                    }}>
                        <div style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '16px',
                            maxWidth: '540px',
                            width: '100%',
                            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
                            overflow: 'hidden'
                        }}>
                            {/* Modal Header */}
                            <div style={{
                                backgroundColor: '#603833',
                                color: '#ffffff',
                                padding: '18px 24px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <i className="bi bi-grid-3x3-gap-fill" style={{ fontSize: '20px' }}></i>
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>
                                        {formData.id ? 'Editar Mesa' : 'Nueva Mesa'}
                                    </h3>
                                </div>
                                <button
                                    onClick={() => setShowModal(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#ffffff',
                                        fontSize: '20px',
                                        cursor: 'pointer',
                                        opacity: 0.8
                                    }}
                                >
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>

                            {/* Modal Form */}
                            <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
                                            Número de Mesa *
                                        </label>
                                        <input
                                            type="text"
                                            name="number"
                                            value={formData.number}
                                            onChange={handleInputChange}
                                            placeholder="Ej: 1, 2, 10, VIP-1"
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '9px 12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
                                            Capacidad (personas)
                                        </label>
                                        <input
                                            type="number"
                                            name="capacity"
                                            value={formData.capacity}
                                            onChange={handleInputChange}
                                            min="1"
                                            max="50"
                                            style={{
                                                width: '100%',
                                                padding: '9px 12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
                                        Nombre Descriptivo
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Ej: Mesa 1, Terraza Esquina, VIP Jardín"
                                        style={{
                                            width: '100%',
                                            padding: '9px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            outline: 'none'
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
                                            Sección
                                        </label>
                                        <input
                                            type="text"
                                            name="section"
                                            value={formData.section}
                                            onChange={handleInputChange}
                                            placeholder="Interior, Terraza, Exterior, Barra"
                                            style={{
                                                width: '100%',
                                                padding: '9px 12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
                                            Estado
                                        </label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            style={{
                                                width: '100%',
                                                padding: '9px 12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                outline: 'none',
                                                backgroundColor: '#ffffff'
                                            }}
                                        >
                                            <option value="available">Disponible</option>
                                            <option value="occupied">Ocupada</option>
                                            <option value="reserved">Reservada</option>
                                            <option value="cleaning">Limpiando</option>
                                            <option value="maintenance">Mantenimiento</option>
                                        </select>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            name="is_vip"
                                            checked={formData.is_vip}
                                            onChange={handleInputChange}
                                            style={{ width: '16px', height: '16px', accentColor: '#603833' }}
                                        />
                                        Mesa VIP
                                    </label>

                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            name="is_active"
                                            checked={formData.is_active}
                                            onChange={handleInputChange}
                                            style={{ width: '16px', height: '16px', accentColor: '#603833' }}
                                        />
                                        Mesa Habilitada
                                    </label>
                                </div>

                                <div style={{ marginBottom: '24px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
                                        Notas Adicionales
                                    </label>
                                    <textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        placeholder="Ej: Ubicación junto a la ventana, toma de corriente cercana..."
                                        rows="2"
                                        style={{
                                            width: '100%',
                                            padding: '9px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            outline: 'none',
                                            resize: 'vertical'
                                        }}
                                    ></textarea>
                                </div>

                                {/* Modal Actions */}
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setShowModal(false)}
                                        style={{
                                            padding: '10px 18px',
                                            backgroundColor: '#f1f5f9',
                                            color: '#475569',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontWeight: '600',
                                            fontSize: '14px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSaving}
                                        style={{
                                            padding: '10px 22px',
                                            backgroundColor: '#603833',
                                            color: '#ffffff',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontWeight: '700',
                                            fontSize: '14px',
                                            cursor: 'pointer',
                                            boxShadow: '0 4px 10px rgba(96, 56, 51, 0.2)'
                                        }}
                                    >
                                        {isSaving ? 'Guardando...' : (formData.id ? 'Actualizar Mesa' : 'Guardar Mesa')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default Mesas;
